/**
 * The app-wide command palette (Ctrl/Cmd+K), ported from the Syncra reference mockup and
 * adapted to the real SPA: navigation between the home dashboard (with its calendar feed),
 * the recordings archive and the settings, joining a room straight from a typed slug, and the
 * identity/language actions. No extra dependency: the list, filtering and keyboard handling
 * are hand-rolled over the Radix dialog primitive the repo already carries (the mockup's cmdk
 * is not in the dependency set).
 *
 * Registered once in the App shell, so the Ctrl/Cmd+K listener covers the home dashboard, the
 * recordings and settings screens and the room alike (room hotkeys ignore ctrl/meta, so there
 * is no conflict).
 */

import {
  ArrowRight,
  CalendarDays,
  Copy,
  FileVideo2,
  Globe,
  Home,
  Link2,
  LogIn,
  LogOut,
  Search,
  Settings as SettingsIcon,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { ComponentType, JSX, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog';
import { changeLanguage, i18n, type AppLanguage } from '../i18n';
import { copyText } from '../room/clipboard';
import type { KeyValueStorage } from '../room/devicePrefs';
import { isValidRoomSlug, roomSlugFromPath } from '../room/roomState';
import { navigateTo } from '../lib/spa';
import { browserSsoStorage } from '../auth/sso';
import {
  checkSession,
  forgetSession,
  startCalendarSignIn,
  calendarSsoConfig,
} from '../calendar/signIn';
import { showToast } from '../calendar/Toast';
import { cn } from '../lib/utils';

interface CommandItem {
  readonly id: string;
  readonly label: string;
  /** Extra lowercase search terms beyond the label. */
  readonly keywords: string;
  readonly icon: ComponentType<{ className?: string }>;
  readonly hint?: string;
  readonly onSelect: () => void;
}

interface CommandGroup {
  readonly id: string;
  readonly heading: string;
  readonly items: readonly CommandItem[];
}

export interface CommandPaletteProps {
  /** Current SPA pathname (from the app shell); drives the in-room actions and navigation. */
  readonly pathname: string;
  /** Injected for tests; defaults to the browser's shared SSO store. */
  readonly storage?: KeyValueStorage | null;
  /** Copy override for tests; defaults to the shared clipboard helper. */
  readonly copyImpl?: (text: string) => Promise<boolean>;
  /** Full-reload override for tests; sign-out resets every screen's in-memory session state. */
  readonly reload?: () => void;
}

export function CommandPalette({
  pathname,
  storage,
  copyImpl,
  reload,
}: CommandPaletteProps): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const store = useMemo(() => storage ?? browserSsoStorage(), [storage]);

  // Ctrl/Cmd+K toggles the palette from any screen; e.repeat and other modifiers are ignored.
  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if (!(event.metaKey || event.ctrlKey)) {
        return;
      }
      if (event.key.toLowerCase() !== 'k' && event.code !== 'KeyK') {
        return;
      }
      event.preventDefault();
      if (event.repeat) {
        return;
      }
      setOpen((previous) => !previous);
    }
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  // Every open starts a fresh search.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
    }
  }, [open]);

  const signedIn = store !== null && checkSession(store, new Date()) === 'signed_in';
  const roomSlug = roomSlugFromPath(pathname);
  const trimmed = query.trim();
  const joinCandidate = isValidRoomSlug(trimmed) ? trimmed : null;

  const doReload =
    reload ??
    ((): void => {
      window.location.assign(window.location.origin + '/');
    });

  function run(item: CommandItem): void {
    setOpen(false);
    item.onSelect();
  }

  function handleSignIn(): void {
    if (store === null) {
      showToast('error', t('sso.notice_storage_blocked'));
      return;
    }
    void startCalendarSignIn(
      calendarSsoConfig(),
      window.location.origin,
      store,
      window.location,
      '/',
    ).catch(() => {
      // staying on the page; the action can be retried
    });
  }

  function handleSignOut(): void {
    if (store !== null) {
      forgetSession(store);
    }
    // The session is shared module- and tab-wide state; a full navigation resets every
    // screen's in-memory identity at once (the dashboard, the palette and the room).
    doReload();
  }

  function goHomeAndScroll(): void {
    if (pathname !== '/') {
      navigateTo('/');
    }
    // The dashboard mounts on the next tick when navigation happened; scrolling is a no-op
    // for guests (the calendar sections render only for signed-in sessions).
    window.setTimeout(() => {
      document.getElementById('dashboard-today')?.scrollIntoView({ block: 'start' });
    }, 0);
  }

  function toggleLanguage(): void {
    const current: AppLanguage = i18n.language === 'ru' ? 'ru' : 'en';
    const next: AppLanguage = current === 'en' ? 'ru' : 'en';
    void changeLanguage(next);
  }

  async function copyRoomLink(): Promise<void> {
    if (roomSlug === null) {
      return;
    }
    const ok = await (copyImpl ?? copyText)(`${window.location.origin}/r/${roomSlug}`);
    showToast(ok ? 'success' : 'error', t(ok ? 'palette.link_copied' : 'palette.copy_failed'));
  }

  const groups: readonly CommandGroup[] = [
    ...(joinCandidate !== null
      ? [
          {
            id: 'join',
            heading: t('palette.group_join'),
            items: [
              {
                id: 'join-room',
                label: t('palette.item_join_room', { slug: joinCandidate }),
                keywords: 'join room call enter',
                icon: ArrowRight,
                onSelect: () => {
                  navigateTo(`/r/${joinCandidate}`);
                },
              },
            ],
          },
        ]
      : []),
    {
      id: 'navigation',
      heading: t('palette.group_navigation'),
      items: [
        {
          id: 'go-dashboard',
          label: t('palette.item_dashboard'),
          keywords: 'dashboard home calendar feed meetings',
          icon: Home,
          onSelect: goHomeAndScroll,
        },
        {
          id: 'go-calendar-feed',
          label: t('palette.item_calendar_feed'),
          keywords: 'calendar feed schedule events today',
          icon: CalendarDays,
          onSelect: goHomeAndScroll,
        },
        {
          id: 'go-recordings',
          label: t('palette.item_recordings'),
          keywords: 'recordings archive downloads',
          icon: FileVideo2,
          onSelect: () => {
            navigateTo('/recordings');
          },
        },
        {
          id: 'go-settings',
          label: t('palette.item_settings'),
          keywords: 'settings preferences devices language account',
          icon: SettingsIcon,
          onSelect: () => {
            navigateTo('/settings');
          },
        },
        {
          id: 'join-by-slug',
          label: t('palette.item_join_home'),
          keywords: 'join room code link call',
          icon: Link2,
          hint: '/r/…',
          onSelect: goHomeAndScroll,
        },
      ],
    },
    {
      id: 'actions',
      heading: t('palette.group_actions'),
      items: [
        {
          id: 'toggle-language',
          label: t('palette.item_language', {
            name: t(`lang.name_${i18n.language === 'ru' ? 'en' : 'ru'}`),
          }),
          keywords: 'language translate en ru english russian',
          icon: Globe,
          onSelect: toggleLanguage,
        },
        ...(roomSlug !== null
          ? [
              {
                id: 'copy-room-link',
                label: t('palette.item_copy_link'),
                keywords: 'copy link invite share url',
                icon: Copy,
                hint: `/r/${roomSlug}`,
                onSelect: () => {
                  void copyRoomLink();
                },
              },
            ]
          : []),
        signedIn
          ? {
              id: 'sign-out',
              label: t('palette.item_sign_out'),
              keywords: 'sign out logout sso',
              icon: LogOut,
              onSelect: handleSignOut,
            }
          : {
              id: 'sign-in',
              label: t('palette.item_sign_in'),
              keywords: 'sign in login sso sso account',
              icon: LogIn,
              onSelect: handleSignIn,
            },
      ],
    },
  ];

  const visibleGroups = groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) =>
        `${item.label} ${item.keywords}`.toLowerCase().includes(trimmed.toLowerCase()),
      ),
    }))
    .filter((group) => group.items.length > 0);

  const flatItems = visibleGroups.flatMap((group) => group.items);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  function onKeyDown(event: ReactKeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (flatItems.length === 0 ? 0 : (index + 1) % flatItems.length));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) =>
        flatItems.length === 0 ? 0 : (index - 1 + flatItems.length) % flatItems.length,
      );
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const item = flatItems[activeIndex];
      if (item !== undefined) {
        run(item);
      }
    }
  }

  let flatIndex = -1;

  return (
    <Dialog
      open={open}
      onOpenChange={(next: boolean) => {
        setOpen(next);
      }}
    >
      <DialogContent
        showCloseButton={false}
        data-testid="command-palette"
        className="top-[18%] max-w-xl translate-y-0 gap-0 overflow-hidden rounded-xl p-0 sm:max-w-xl"
      >
        <DialogTitle className="sr-only">{t('palette.title')}</DialogTitle>
        <DialogDescription className="sr-only">{t('palette.description')}</DialogDescription>
        <div className="flex h-12 items-center gap-2.5 border-b border-border px-3.5">
          <Search aria-hidden className="size-4 shrink-0 text-muted-foreground" />
          <input
            data-testid="command-input"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            onKeyDown={onKeyDown}
            placeholder={t('palette.placeholder')}
            aria-label={t('palette.placeholder')}
            autoComplete="off"
            spellCheck={false}
            className="h-full min-w-0 flex-1 bg-transparent text-[14px] outline-none placeholder:text-muted-foreground"
          />
        </div>

        <div
          role="listbox"
          aria-label={t('palette.title')}
          className="scroll-thin max-h-[360px] overflow-y-auto p-2"
          data-testid="command-list"
        >
          {flatItems.length === 0 ? (
            <div className="flex flex-col items-center gap-1 px-4 py-6" data-testid="command-empty">
              <span className="text-[13px] font-medium text-foreground">{t('palette.empty')}</span>
              <span className="text-[12px] text-muted-foreground">{t('palette.empty_hint')}</span>
            </div>
          ) : (
            visibleGroups.map((group) => (
              <div
                key={group.id}
                role="group"
                aria-label={group.heading}
                className="mb-1 last:mb-0"
              >
                <p className="px-2.5 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  {group.heading}
                </p>
                {group.items.map((item) => {
                  flatIndex += 1;
                  const index = flatIndex;
                  const active = index === activeIndex;
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="option"
                      aria-selected={active}
                      data-testid={`command-item-${item.id}`}
                      onMouseMove={() => {
                        setActiveIndex(index);
                      }}
                      onClick={() => {
                        run(item);
                      }}
                      className={cn(
                        'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13.5px]',
                        active ? 'bg-accent text-foreground' : 'text-foreground/90',
                      )}
                    >
                      <Icon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                      <span className="min-w-0 flex-1 truncate">{item.label}</span>
                      {item.hint !== undefined ? (
                        <span className="shrink-0 font-mono text-[11.5px] text-muted-foreground">
                          {item.hint}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="border-t border-border/70 px-3.5 py-2 text-[11px] text-muted-foreground">
          {t('palette.footer_hints')}
        </div>
      </DialogContent>
    </Dialog>
  );
}
